/**
 * FloorLayoutPage — Phase 2 page
 * ─────────────────────────────────────────────────────────────────────────────
 * Visual floor map of in-stock bales grouped by supplier ("swimlanes"), with
 * each lane colored by quality_group A/B/C/D distribution. Backed by
 * GET /api/v1/bale-selection/floor-map.
 *
 * V1 layout: vertical swimlanes (largest supplier first), each lane shows:
 *   - supplier name + bale count + total kg
 *   - 5-segment color bar (A green / B blue / C orange / D red / UNKNOWN gray)
 *   - up to 5 sample bale_no for the tooltip
 *
 * V2 will overlay actual godown XY coordinates if godowns table gets a
 * coords column. V1 is enough to spot bad batches.
 *
 * Spec: blueprint Sec 28-29 (floor visualization).
 */
import { useState } from 'react'
import {
  Alert, Card, Col, Empty, Row, Select, Space, Statistic, Switch, Tag,
  Tooltip, Typography,
} from 'antd'
import { ReloadOutlined, AppstoreOutlined } from '@ant-design/icons'
import { useQuery } from '@tanstack/react-query'
import PageHeader from '@/shared/components/PageHeader'
import { baleSelectionApi } from '@/features/baleManagement/api'
import { plantsApi } from '@/features/masters/plants/api'

const { Text } = Typography

// ── Types mirroring backend FloorMapResponse ────────────────────────────────
type GroupCounts = { A: number; B: number; C: number; D: number; UNKNOWN: number }

type CottonGrades = {
  E1: number; E2: number; A: number; B: number; C: number; D: number;
  R: number; UNGRADED: number;
}

type Swimlane = {
  supplier_id: string | null
  supplier_name: string
  bale_count: number
  total_weight_kg: number
  groups: GroupCounts
  cotton_grades?: CottonGrades
  sample_bales: string[]
}

type FloorMap = {
  plant_id: string
  total_bales: number
  total_weight_kg: number
  suppliers: Swimlane[]
}

type Plant = { id: string; name: string }

// ── Quality-group palette (same as VerdictBadge family) ──────────────────────
const GROUP_COLOR: Record<keyof GroupCounts, string> = {
  A: '#52c41a',         // GOOD — premium
  B: '#1677ff',         // ACCEPTABLE
  C: '#faad14',         // WARNING — coarse
  D: '#ff4d4f',         // BAD / REJECT
  UNKNOWN: '#bfbfbf',   // not yet classified
}

// Cotton grade palette (Sprint B — Sec 1 mental model)
const COTTON_GRADE_COLOR: Record<keyof CottonGrades, string> = {
  E1: '#722ed1', E2: '#9254de',
  A:  '#52c41a', B:  '#1677ff',
  C:  '#faad14', D:  '#fa8c16',
  R:  '#8c8c8c', UNGRADED: '#d9d9d9',
}

// ── 5-segment proportional bar ───────────────────────────────────────────────
function GroupBar({ groups, total }: { groups: GroupCounts; total: number }) {
  if (total === 0) return null
  const pct = (n: number) => (n / total) * 100
  const order: (keyof GroupCounts)[] = ['A', 'B', 'C', 'D', 'UNKNOWN']
  return (
    <div style={{
      display: 'flex', height: 28, borderRadius: 6, overflow: 'hidden',
      border: '1px solid #f0f0f0',
    }}>
      {order.map(k => groups[k] > 0 ? (
        <Tooltip key={k} title={`${k}: ${groups[k]} bales`}>
          <div style={{
            width: `${pct(groups[k])}%`,
            background: GROUP_COLOR[k],
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: '#fff', fontSize: 11, fontWeight: 600,
          }}>
            {groups[k] >= 3 ? `${k} ${groups[k]}` : ''}
          </div>
        </Tooltip>
      ) : null)}
    </div>
  )
}

// ── Cotton-grade bar (E1/E2/A/B/C/D/R) ───────────────────────────────────────
function CottonGradeBar({ grades, total }: { grades: CottonGrades; total: number }) {
  if (total === 0) return null
  const pct = (n: number) => (n / total) * 100
  const order: (keyof CottonGrades)[] = ['E1', 'E2', 'A', 'B', 'C', 'D', 'R', 'UNGRADED']
  return (
    <div style={{
      display: 'flex', height: 20, borderRadius: 4, overflow: 'hidden',
      border: '1px solid #f0f0f0',
    }}>
      {order.map(k => grades[k] > 0 ? (
        <Tooltip key={k} title={`${k}: ${grades[k]} bales`}>
          <div style={{
            width: `${pct(grades[k])}%`,
            background: COTTON_GRADE_COLOR[k],
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: '#fff', fontSize: 10, fontWeight: 600,
          }}>
            {grades[k] >= 5 ? `${k} ${grades[k]}` : ''}
          </div>
        </Tooltip>
      ) : null)}
    </div>
  )
}

// ── Swimlane card ────────────────────────────────────────────────────────────
function SwimlaneCard({ lane }: { lane: Swimlane }) {
  const tooltip = lane.sample_bales.length > 0
    ? `Sample bales: ${lane.sample_bales.join(', ')}${lane.sample_bales.length === 5 ? ' +' : ''}`
    : ''
  const hasGradedBales = lane.cotton_grades
    && (lane.cotton_grades.E1 + lane.cotton_grades.E2 + lane.cotton_grades.A
        + lane.cotton_grades.B + lane.cotton_grades.C + lane.cotton_grades.D
        + lane.cotton_grades.R) > 0
  return (
    <Card size="small" style={{ marginBottom: 12 }} styles={{ body: { padding: 12 } }}>
      <Tooltip title={tooltip} placement="topLeft">
        <Space style={{ width: '100%', justifyContent: 'space-between', marginBottom: 8 }}>
          <Space size={8}>
            <Text strong style={{ fontSize: 14 }}>{lane.supplier_name}</Text>
            <Tag color="blue">{lane.bale_count} bales</Tag>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {(lane.total_weight_kg / 1000).toFixed(2)} T
            </Text>
          </Space>
        </Space>
        <Space direction="vertical" size={4} style={{ width: '100%' }}>
          <GroupBar groups={lane.groups} total={lane.bale_count} />
          {lane.cotton_grades && hasGradedBales && (
            <CottonGradeBar grades={lane.cotton_grades} total={lane.bale_count} />
          )}
        </Space>
      </Tooltip>
    </Card>
  )
}

// ── Page ─────────────────────────────────────────────────────────────────────
export default function FloorLayoutPage() {
  const [plantId, setPlantId] = useState<string | undefined>()
  const [includePending, setIncludePending] = useState<boolean>(true)

  const plantsQ = useQuery<Plant[]>({
    queryKey: ['plants', 'all'],
    queryFn: async () => {
      const res = await plantsApi.list()
      const r = res as unknown as { items?: Plant[]; data?: Plant[] } | Plant[]
      if (Array.isArray(r)) return r
      return r.items ?? r.data ?? []
    },
  })

  if (!plantId && plantsQ.data && plantsQ.data.length > 0) {
    setPlantId(plantsQ.data[0].id)
  }

  const mapQ = useQuery<FloorMap>({
    queryKey: ['floor-map', plantId, includePending],
    queryFn: () => baleSelectionApi.floorMap(plantId!, includePending),
    enabled: Boolean(plantId),
    refetchOnWindowFocus: false,
  })

  const data = mapQ.data

  // Aggregate group counts across all suppliers for the legend chips
  const overall: GroupCounts = data
    ? data.suppliers.reduce(
      (acc, sl) => ({
        A: acc.A + sl.groups.A,
        B: acc.B + sl.groups.B,
        C: acc.C + sl.groups.C,
        D: acc.D + sl.groups.D,
        UNKNOWN: acc.UNKNOWN + sl.groups.UNKNOWN,
      }),
      { A: 0, B: 0, C: 0, D: 0, UNKNOWN: 0 } as GroupCounts,
    )
    : { A: 0, B: 0, C: 0, D: 0, UNKNOWN: 0 }

  return (
    <div style={{ padding: 16 }}>
      <PageHeader
        title="Floor Layout"
        subtitle="In-stock bales grouped by supplier and colored by quality group"
        extra={
          <Space>
            <Select
              value={plantId}
              onChange={setPlantId}
              placeholder="Plant"
              style={{ width: 180 }}
              loading={plantsQ.isLoading}
              options={(plantsQ.data || []).map(p => ({ label: p.name, value: p.id }))}
            />
            <Space size={4}>
              <Switch
                checked={includePending}
                onChange={setIncludePending}
                size="small"
              />
              <Text style={{ fontSize: 12 }}>Show PENDING QC</Text>
            </Space>
            <Tooltip title="Refresh">
              <ReloadOutlined
                onClick={() => mapQ.refetch()}
                style={{ fontSize: 16, cursor: 'pointer' }}
                spin={mapQ.isFetching}
              />
            </Tooltip>
          </Space>
        }
      />

      {!plantId && (
        <Alert message="Pick a plant to begin." type="info" showIcon style={{ marginTop: 16 }} />
      )}

      {mapQ.isError && (
        <Alert
          message="Failed to load floor map"
          description={(mapQ.error as Error)?.message}
          type="error" showIcon style={{ marginTop: 16 }}
        />
      )}

      {data && (
        <>
          {/* ── KPI strip ─────────────────────────────────────────────────── */}
          <Row gutter={16} style={{ marginTop: 16 }}>
            <Col xs={12} md={6}>
              <Card>
                <Statistic
                  title="Total bales"
                  value={data.total_bales}
                  prefix={<AppstoreOutlined />}
                />
              </Card>
            </Col>
            <Col xs={12} md={6}>
              <Card>
                <Statistic
                  title="Total weight"
                  value={(data.total_weight_kg / 1000).toFixed(2)}
                  suffix="T"
                />
              </Card>
            </Col>
            <Col xs={12} md={6}>
              <Card>
                <Statistic
                  title="Suppliers on floor"
                  value={data.suppliers.length}
                />
              </Card>
            </Col>
            <Col xs={12} md={6}>
              <Card>
                <Statistic
                  title="Classified"
                  value={data.total_bales > 0
                    ? Math.round(((data.total_bales - overall.UNKNOWN) / data.total_bales) * 100)
                    : 0}
                  suffix="%"
                />
                <Text type="secondary" style={{ fontSize: 11 }}>
                  Run backfill to classify the rest
                </Text>
              </Card>
            </Col>
          </Row>

          {/* ── Legend ────────────────────────────────────────────────────── */}
          <Card style={{ marginTop: 16 }} styles={{ body: { padding: 12 } }}>
            <Space size={16} wrap>
              <Text strong style={{ fontSize: 13 }}>Legend:</Text>
              {(['A', 'B', 'C', 'D', 'UNKNOWN'] as const).map(g => (
                <Space key={g} size={6}>
                  <div style={{
                    width: 16, height: 16, borderRadius: 3,
                    background: GROUP_COLOR[g],
                  }} />
                  <Text style={{ fontSize: 12 }}>
                    Group {g} <Text type="secondary">({overall[g]})</Text>
                  </Text>
                </Space>
              ))}
            </Space>
          </Card>

          {/* ── Swimlanes ─────────────────────────────────────────────────── */}
          <div style={{ marginTop: 16 }}>
            {data.suppliers.length === 0 ? (
              <Empty
                description="No in-stock bales for this plant"
                style={{ padding: 48 }}
              />
            ) : (
              data.suppliers.map(lane => (
                <SwimlaneCard key={lane.supplier_id ?? 'unknown'} lane={lane} />
              ))
            )}
          </div>
        </>
      )}
    </div>
  )
}
